import { scaleBand, scaleLinear } from 'd3-scale';
import { Box } from '@mentimeter/ragnar-ui/box';
import type { Visualization } from '../types';
import { ScalesArea } from '../scales/ScalesArea';
import { SvgWithFont } from '../utils/SvgWithFont';
import { AxisBottom } from './AxisBottom';
import { getDisplayValues } from './utils';
import { CorrectAnswer } from './CorrectAnswer';

export type GuessTheNumberData = Record<number, number>;

interface GuessTheNumberProps
  extends Omit<
    Visualization<GuessTheNumberData, never>,
    'data' | 'staticOptions'
  > {
  rangeMin: number;
  rangeMax: number;
  data: GuessTheNumberData;
  responseRangeScale: number;
  correctAnswer: number;
  shouldShowCorrectAnswer: boolean;
}

const AXIS_BOTTOM_HEIGHT = 50;
const SVG_INNER_WIDTH_PADDING = 20;

export const GuessTheNumber = ({
  width,
  height,
  rangeMin,
  rangeMax,
  responseRangeScale,
  data: rawData,
  fontFamily,
  correctAnswer,
  shouldShowCorrectAnswer,
  textColor,
  colors,
  marginLeft = 0,
  marginRight = 0,
  marginBottom = 0,
  marginTop = 0,
}: GuessTheNumberProps) => {
  const rootHeight = Math.max(height, 0);
  const rootWidth = Math.max(width, 0);

  const svgWidth = Math.max(rootWidth - marginLeft - marginRight, 0);
  const svgHeight = Math.max(rootHeight - marginTop - marginBottom, 0);

  const chartWidth = svgWidth - SVG_INNER_WIDTH_PADDING * 2;

  const displayValues = getDisplayValues(
    rangeMin,
    rangeMax,
    responseRangeScale,
  );

  const maxScore = Math.max(...Object.values(rawData)) || 0;
  const hasData = Object.keys(rawData).length > 0;
  const yScale = scaleLinear()
    .domain([0, maxScore])
    .range([svgHeight, AXIS_BOTTOM_HEIGHT]);

  const rangeMaxWithoutScale = Math.ceil(
    (rangeMax - rangeMin) / responseRangeScale,
  );

  const xScaleWithoutRangeScale = scaleLinear()
    .domain([0, rangeMaxWithoutScale])
    .range([0, chartWidth]);

  const xScale = scaleLinear()
    .domain([rangeMin, rangeMax])
    .range([0, chartWidth]);

  const discreteScale = scaleBand()
    .domain(displayValues)
    .range([0, chartWidth]);

  const tickWidth = discreteScale.bandwidth();

  return (
    <Box
      width={rootWidth}
      height={rootHeight}
      justifyContent="center"
      alignItems="center"
      aria-hidden
    >
      <SvgWithFont width={svgWidth} height={svgHeight} fontFamily={fontFamily}>
        <g transform={`translate(${SVG_INNER_WIDTH_PADDING}, 0)`}>
          {hasData && (
            <ScalesArea
              fill={colors[0]}
              scores={rawData}
              yScale={yScale}
              xScale={xScaleWithoutRangeScale}
              showDistribution={false}
              disableAnimations={false}
              paddingBottom={AXIS_BOTTOM_HEIGHT}
            />
          )}
          <CorrectAnswer
            height={svgHeight}
            width={tickWidth}
            x={xScale(correctAnswer)}
            shouldShow={shouldShowCorrectAnswer}
          />
          <AxisBottom
            y={svgHeight - AXIS_BOTTOM_HEIGHT}
            xScale={xScale}
            numberOfTicksTarget={10}
            textColor={textColor}
            lineColor={colors[0]}
          />
        </g>
      </SvgWithFont>
    </Box>
  );
};
